import {
  EkycRequestStatus,
  ScreeningStatus,
  ServiceRequestStatus,
  ServiceRequestType,
} from 'src/app/admin/assisted-ekyc/shared/ekyc-request.enum';
import { IEkycRequestDetail } from 'src/app/admin/assisted-ekyc/shared/ekyc-request.model';

/**
 * Seed requests for the showcase — contracts-1 §11.5.
 *
 * Twenty records, deliberately spread so every screen has content and every action in
 * contracts-2 Appendix D is reachable. All personal data is synthetic (SRS §9.1 forbids
 * production NID images and biometrics outside production).
 */

const OTHER_MAKER = { id: 'USR-MAKER-002', name: 'Shirin Akter' };
const OTHER_CHECKER = { id: 'USR-CHECKER-002', name: 'Kamrul Hasan' };
const TEST_MAKER = { id: 'USR-MAKER-001', name: 'Tanvir Ahmed' };
const TEST_CHECKER = { id: 'USR-CHECKER-001', name: 'Nusrat Jahan' };

const PLACEHOLDER_PHOTO = 'assets/mock/portrait-placeholder.svg';
const PLACEHOLDER_NID = 'assets/mock/nid-placeholder.svg';

function iso(daysAgo: number, hour = 10): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hour, 15, 0, 0);
  return date.toISOString();
}

let sequence = 0;

interface SeedOptions {
  applicantName: string;
  status: EkycRequestStatus;
  ekycType: 'REGULAR' | 'SIMPLIFIED';
  daysAgo: number;
  makerUserId?: string;
  makerName?: string;
  branchCheckerUserId?: string | null;
  cogbCheckerUserId?: string | null;
  riskScore?: number;
  riskBand?: 'LOW' | 'MEDIUM' | 'HIGH';
  screeningStatus?: ScreeningStatus;
  pepConfirmed?: boolean;
  faceMatchScore?: number;
  accountClass?: 'CONVENTIONAL' | 'ISLAMIC';
  returnCount?: number;
  createdAccount?: string | null;
  cbsAttemptCount?: number;
  cbsLastFailureReason?: string | null;
  requestKind?: 'NEW' | 'UPGRADE';
  upgradeStatus?: 'NONE' | 'IN_PROGRESS' | 'PENDING_APPROVAL' | 'UPGRADED';
  requestTypeLabel?: string;
  progressLabel?: string;
  resumeStepKey?: string;
  services?: Partial<Record<ServiceRequestType, ServiceRequestStatus>>;
  checkerRemark?: string;
  cogbRemark?: string;
}

function build(options: SeedOptions): IEkycRequestDetail {
  sequence += 1;
  const requestId = `AEK-2026-${String(sequence).padStart(4, '0')}`;
  const nid = `19${88 + (sequence % 12)}${String(1000000 + sequence * 7919).slice(0, 7)}`;
  const firstName = options.applicantName.split(' ')[0].toLowerCase();
  const isSimplified = options.ekycType === 'SIMPLIFIED';
  const makerUserId = options.makerUserId ?? OTHER_MAKER.id;
  const makerName = options.makerName ?? OTHER_MAKER.name;
  const riskScore = options.riskScore ?? 8;
  const riskBand = options.riskBand ?? 'LOW';
  const screeningStatus = options.screeningStatus ?? ScreeningStatus.CLEAR;
  const faceMatchScore = options.faceMatchScore ?? 92;
  const accountClass = options.accountClass ?? 'CONVENTIONAL';
  const branchCode = 'BR-0101';
  const branchName = 'Motijheel Branch';

  const services = Object.entries(options.services ?? {}).map(([type, status], index) => ({
    serviceRequestId: `${requestId}-SVC-${index + 1}`,
    ekycRequestId: requestId,
    serviceType: type as ServiceRequestType,
    idempotencyKey: `${requestId}:${type}`,
    status: status as ServiceRequestStatus,
    attemptCount: status === ServiceRequestStatus.FAILED ? 3 : 1,
    attempts:
      status === ServiceRequestStatus.FAILED
        ? [1, 2, 3].map((n) => ({
            attemptNo: n,
            at: iso(options.daysAgo, 11 + n),
            outcome: 'FAILED',
            reasonCode: 'CMS_TIMEOUT',
            rawResponse: '{"error":"upstream timeout after 30s"}',
          }))
        : [
            {
              attemptNo: 1,
              at: iso(options.daysAgo, 12),
              outcome: status === ServiceRequestStatus.ACKNOWLEDGED ? 'ACK' : 'PENDING',
              reasonCode: null,
              rawResponse: null,
            },
          ],
    deliveryBranchCode: branchCode,
    deliveryBranchName: branchName,
    ...(type === ServiceRequestType.DEBIT_CARD
      ? { cardProductTypeCode: 'CARD-VISA-CLASSIC', cardProductTypeName: 'Visa Classic Debit', nameOnCard: options.applicantName.toUpperCase() }
      : {}),
    ...(type === ServiceRequestType.CHEQUE_BOOK ? { chequeBookLeafCount: 25, chequeBookProductType: 'Savings 25-leaf' } : {}),
    ...(type === ServiceRequestType.INTERNET_BANKING
      ? { ibConsentAccepted: true, ibNotificationMobile: `017${String(10000000 + sequence * 31).slice(0, 8)}`, ibNotificationEmail: `${firstName}@example.com` }
      : {}),
  }));

  return {
    requestId,
    applicantName: options.applicantName,
    dateOfBirth: `19${88 + (sequence % 12)}-0${1 + (sequence % 9)}-1${sequence % 9}`,
    oldNid: sequence % 3 === 0 ? String(1234567890 + sequence) : null,
    smartNid: nid,
    email: `${firstName}@example.com`,
    phoneNumber: `017${String(10000000 + sequence * 31).slice(0, 8)}`,
    cifNumber: options.createdAccount ? `CIF${100000 + sequence}` : null,
    accountClass,
    productTypeName: isSimplified ? 'Simple Savings Account' : accountClass === 'ISLAMIC' ? 'Al-Amin Mudaraba Savings' : 'Premium Savings Account',
    branchCode,
    branchName,
    selectedAccount: null,
    createdAccount: options.createdAccount ?? null,
    ekycType: options.ekycType,
    requestKind: options.requestKind ?? 'NEW',
    upgradeStatus: options.upgradeStatus ?? 'NONE',
    requestTypeLabel: options.requestTypeLabel ?? (options.status === EkycRequestStatus.DRAFT ? 'Draft Request' : null),
    progressLabel: options.progressLabel ?? (options.status === EkycRequestStatus.DRAFT ? 'Partially completed' : null),
    resumeStepKey: options.resumeStepKey ?? null,
    riskScore,
    riskBand,
    screeningStatus,
    requestedTime: iso(options.daysAgo),
    status: options.status,
    makerUserId,
    makerName,
    branchCheckerUserId: options.branchCheckerUserId ?? null,
    cogbCheckerUserId: options.cogbCheckerUserId ?? null,
    returnCount: options.returnCount ?? 0,

    selectedRegion: 'BANGLADESH',
    consentAcceptedAt: iso(options.daysAgo, 9),
    consentVersion: 'TC-2026.1',

    identityType: 'NID',
    ekycDetailsResponse: {
      userIdentity: nid,
      nationalIdNumber: nid,
      oldNid: null,
      dateOfBirth: `19${88 + (sequence % 12)}-0${1 + (sequence % 9)}-1${sequence % 9}`,
      gender: sequence % 2 === 0 ? 'Female' : 'Male',
      fullNameEnglish: options.applicantName,
      fullNameLocal: options.applicantName,
      fathersNameEnglish: `Abdul ${options.applicantName.split(' ').slice(-1)[0]}`,
      fathersNameLocal: `Abdul ${options.applicantName.split(' ').slice(-1)[0]}`,
      mothersNameEnglish: 'Rahima Begum',
      mothersNameLocal: 'Rahima Begum',
      spouseNameEnglish: null,
      spouseNameLocal: null,
      incomeSource: 'Salary',
      monthlyIncome: 45000 + sequence * 1500,
      occupation: 'Private service',
      bloodGroup: 'B+',
      postalCode: '1000',
      postOffice: 'Dhaka GPO',
      presentAddressEnglish: 'House 12, Road 4, Dhanmondi, Dhaka',
      presentAddressLocal: 'House 12, Road 4, Dhanmondi, Dhaka',
      permanentAddressEnglish: 'Village Sonapur, Comilla',
      permanentAddressLocal: 'Village Sonapur, Comilla',
    },
    comparisonData: [
      { fieldName: 'Customer Name', ekycValue: options.applicantName, cbsValue: options.applicantName, matched: 100 },
      { fieldName: "Father's Name", ekycValue: `Abdul ${options.applicantName.split(' ').slice(-1)[0]}`, cbsValue: `Abdul ${options.applicantName.split(' ').slice(-1)[0]}`, matched: 100 },
      { fieldName: "Mother's Name", ekycValue: 'Rahima Begum', cbsValue: sequence % 5 === 0 ? 'Rahima Khatun' : 'Rahima Begum', matched: sequence % 5 === 0 ? 72 : 100 },
    ],
    faceMatch: {
      selfieUrl: PLACEHOLDER_PHOTO,
      nidPhotoUrl: PLACEHOLDER_NID,
      score: faceMatchScore,
      threshold: 75,
      livenessPassed: true,
      manualReviewRequired: faceMatchScore < 75,
      capturedAt: iso(options.daysAgo, 9),
    },
    screening: {
      status: screeningStatus,
      screenedAt: iso(options.daysAgo, 10),
      reference: `SCR-${requestId}`,
      holdReason: screeningStatus === ScreeningStatus.POTENTIAL_MATCH ? 'Name similarity against UN consolidated list — under review by AML' : null,
      pepConfirmed: options.pepConfirmed ?? false,
    },

    passportNumber: null,
    permanentAddress: 'Village Sonapur, Comilla',
    presentAddress: 'House 12, Road 4, Dhanmondi, Dhaka',
    gender: sequence % 2 === 0 ? 'Female' : 'Male',
    maritalStatus: sequence % 3 === 0 ? 'MARRIED' : 'UNMARRIED',
    spouseName: sequence % 3 === 0 ? 'Farhana Akter' : null,
    religion: 'Islam',
    profession: 'Salaried — private sector',
    occupationCategoryName: isSimplified ? null : 'Private Service',
    sourceOfIncome: isSimplified ? null : 'Salary',
    sectorName: isSimplified ? null : 'Manufacturing',
    monthlyIncomeInBdt: 45000 + sequence * 1500,
    tinNumber: isSimplified ? null : `TIN${400000 + sequence}`,

    accountInformation: {
      productTypeCode: isSimplified ? 'PRD-SIMPLE' : 'PRD-PREMIUM',
      productTypeName: isSimplified ? 'Simple Savings Account' : 'Premium Savings Account',
      monthlyTransactionAmount: isSimplified ? 'Up to BDT 100,000' : 'More than BDT 100,000',
      isSelectedTransactionLimitExtra: !isSimplified,
      monthlyTransactionNumber: isSimplified ? 8 : 30,
      divisionName: 'Dhaka',
      districtName: 'Dhaka',
      thanaOrUpozillaName: 'Motijheel',
      branchCode,
      branchName,
    },

    additionalInfo: {
      items: isSimplified
        ? [{ displayName: 'Signature', fieldValue: PLACEHOLDER_NID, fieldType: 'file' }]
        : [
            { displayName: 'TIN Certificate', fieldValue: PLACEHOLDER_NID, fieldType: 'file' },
            { displayName: 'Tax Acknowledgement Receipt', fieldValue: PLACEHOLDER_NID, fieldType: 'file' },
            { displayName: 'Proof of Address', fieldValue: PLACEHOLDER_NID, fieldType: 'file' },
            { displayName: 'Signature', fieldValue: PLACEHOLDER_NID, fieldType: 'file' },
          ],
    },
    fatcaInformation: isSimplified
      ? null
      : { isUsResident: false, isUsCitizen: false, hasUsGreenCard: false, hasUsRegisteredPhone: false, usRegisteredPhoneNo: null, ssn: null },
    personalAssessment: {
      isPublicFigure: options.pepConfirmed ?? false,
      isRelatedToPublicFigure: false,
    },
    nomineeInformation: isSimplified
      ? []
      : [
          {
            nomineeType: 'PRIMARY',
            name: 'Sadia Islam',
            dateOfBirth: '1996-04-11',
            documentType: 'NID',
            nidNumber: String(1990000000 + sequence),
            relationship: 'Sister',
            gender: 'FEMALE',
            religionCode: 'ISLAM',
            religion: 'Islam',
            share: 100,
            mobileNumber: '01755500011',
            contactAddress: 'House 12, Road 4, Dhanmondi, Dhaka',
            nomineePhoto: PLACEHOLDER_PHOTO,
            nomineeNidPhoto: PLACEHOLDER_NID,
            isMinor: false,
            presentAddress: { roadOrVillage: 'Road 4', district: 'Dhaka', subDistrict: 'Dhanmondi', postOffice: 'Dhaka GPO', postCode: '1000' },
            permanentAddress: { roadOrVillage: 'Sonapur', district: 'Comilla', subDistrict: 'Sadar', postOffice: 'Comilla', postCode: '3500' },
            legalGuardian: null,
          },
        ],
    referrerInformation: isSimplified ? null : { referrerName: 'Mizanur Rahman', referrerMobileNumber: '01712345678', referrerEmailAddress: null },
    serviceRequests: services,

    transactionProfile: {
      transactionProfileCode: isSimplified ? 'TP-001' : 'TP-002',
      description: isSimplified ? 'Simplified eKYC — monthly turnover up to BDT 100,000' : 'Regular KYC — monthly turnover above BDT 100,000',
      nextTpReviewDate: iso(-365, 10),
      sectorCode: 'SEC-01',
      sbsCode: 'SBS-1101',
      ekycType: options.ekycType,
      slabDescription: isSimplified ? 'Up to BDT 100,000' : 'More than BDT 100,000',
      entries: [
        {
          transactionProfileTypeName: 'Cash Entry',
          maxCreditAmountPerTransaction: isSimplified ? 50000 : 500000,
          maxDebitAmountPerTransaction: isSimplified ? 50000 : 500000,
          monthlyTotalCreditTxnAmount: isSimplified ? 100000 : 2500000,
          monthlyTotalCreditTxnCount: isSimplified ? 10 : 40,
          monthlyTotalDebitTxnAmount: isSimplified ? 100000 : 2500000,
          monthlyTotalDebitTxnCount: isSimplified ? 10 : 40,
          transactionProfileMandatory: true,
        },
        {
          transactionProfileTypeName: 'Transfer Entry',
          maxCreditAmountPerTransaction: isSimplified ? 40000 : 1000000,
          maxDebitAmountPerTransaction: isSimplified ? 40000 : 1000000,
          monthlyTotalCreditTxnAmount: isSimplified ? 80000 : 5000000,
          monthlyTotalCreditTxnCount: isSimplified ? 8 : 60,
          monthlyTotalDebitTxnAmount: isSimplified ? 80000 : 5000000,
          monthlyTotalDebitTxnCount: isSimplified ? 8 : 60,
          transactionProfileMandatory: !isSimplified,
        },
      ],
      cashTrnCode: 'CSH',
      cashCrTrnCount: isSimplified ? 10 : 40,
      cashCrTrnAmtMonthly: isSimplified ? 100000 : 2500000,
      cashDrTrnCount: isSimplified ? 10 : 40,
      cashDrTrnAmtMonthly: isSimplified ? 100000 : 2500000,
      trfTrnCode: 'TRF',
      trfCrTrnCount: isSimplified ? 8 : 60,
      trfCrTrnAmtMonthly: isSimplified ? 80000 : 5000000,
      trfDrTrnCount: isSimplified ? 8 : 60,
      trfDrTrnAmtMonthly: isSimplified ? 80000 : 5000000,
    },

    riskGradingDetails: [
      { riskGradingCode: 'RG-001', description: 'Customer Type', riskGradingScoreType: 'GENERAL', selectedScoreCode: 'RGS-101', selectedScoreDescription: 'Individual — Bangladeshi resident', score: 1, weight: 1, contribution: 1 },
      { riskGradingCode: 'RG-002', description: 'Occupation / Profession', riskGradingScoreType: 'GENERAL', selectedScoreCode: 'RGS-202', selectedScoreDescription: 'Salaried — private sector', score: 1, weight: 1, contribution: 1 },
      { riskGradingCode: 'RG-003', description: 'Geographic Location', riskGradingScoreType: 'GENERAL', selectedScoreCode: 'RGS-301', selectedScoreDescription: 'Metropolitan — low risk', score: 1, weight: 1, contribution: 1 },
      { riskGradingCode: 'RG-004', description: 'Product Type', riskGradingScoreType: 'GENERAL', selectedScoreCode: 'RGS-401', selectedScoreDescription: 'Savings account', score: 1, weight: 1, contribution: 1 },
      { riskGradingCode: 'RG-005', description: 'Expected Transaction Volume', riskGradingScoreType: 'RANGE', selectedScoreCode: isSimplified ? 'RGS-501' : 'RGS-502', selectedScoreDescription: isSimplified ? 'Up to BDT 100,000 per month' : 'BDT 100,001 – 500,000 per month', score: isSimplified ? 1 : 2, weight: 1, contribution: isSimplified ? 1 : 2 },
      { riskGradingCode: 'RG-006', description: 'Delivery Channel', riskGradingScoreType: 'GENERAL', selectedScoreCode: 'RGS-601', selectedScoreDescription: 'Branch assisted (this portal)', score: 1, weight: 1, contribution: 1 },
      { riskGradingCode: 'RG-007', description: 'PEP / Influential Person', riskGradingScoreType: 'BOOLEAN', selectedScoreCode: options.pepConfirmed ? 'RGS-702' : 'RGS-701', selectedScoreDescription: options.pepConfirmed ? 'PEP, influential person or close associate' : 'Not a PEP or influential person', score: options.pepConfirmed ? 8 : 0, weight: 1, contribution: options.pepConfirmed ? 8 : 0 },
    ],
    riskModelVersion: 'RM-2026.1',
    riskOverride: null,

    documentInformation: [
      { type: 'NID_FRONT', path: PLACEHOLDER_NID, scanStatus: 'CLEAN' },
      { type: 'NID_BACK', path: PLACEHOLDER_NID, scanStatus: 'CLEAN' },
      { type: 'CUSTOMER_PHOTO', path: PLACEHOLDER_PHOTO, scanStatus: 'CLEAN' },
    ],
    additionalDocuments: [],
    branchInformation: { accountType: isSimplified ? 'Simplified Savings' : 'Regular Savings', branchName, branchDistrict: 'Dhaka', branchDivision: 'Dhaka' },
    makerRemarks: [{ remarks: 'Captured at counter. Customer present with original NID.', userName: makerName, at: iso(options.daysAgo, 10) }],
    checkerRemarks: options.checkerRemark ? [{ remarks: options.checkerRemark, userName: TEST_CHECKER.name, at: iso(options.daysAgo, 14) }] : [],
    cogbRemarks: options.cogbRemark ? [{ remarks: options.cogbRemark, userName: 'Rezaul Karim', at: iso(options.daysAgo, 16) }] : [],
    stateHistory: [
      { from: null, to: EkycRequestStatus.DRAFT, actor: makerName, role: 'MAKER', at: iso(options.daysAgo, 9), remarks: null },
      ...(options.status === EkycRequestStatus.DRAFT
        ? []
        : [{ from: EkycRequestStatus.DRAFT, to: options.status, actor: makerName, role: 'MAKER', at: iso(options.daysAgo, 10), remarks: null }]),
    ],
    cbsPrefillFailed: false,
    cbsAttemptCount: options.cbsAttemptCount ?? 0,
    cbsLastFailureReason: options.cbsLastFailureReason ?? null,
  };
}

export function buildEkycRequestSeed(): IEkycRequestDetail[] {
  sequence = 0;
  return [
    // 1-3 Drafts owned by test-maker — resumable in the wizard
    build({
      applicantName: 'Arif Hossain',
      status: EkycRequestStatus.DRAFT,
      ekycType: 'REGULAR',
      daysAgo: 1,
      makerUserId: TEST_MAKER.id,
      makerName: TEST_MAKER.name,
      requestTypeLabel: 'Draft Request',
      progressLabel: 'Partially completed - stopped at FATCA',
      resumeStepKey: 'fatca',
    }),
    build({ applicantName: 'Mahmuda Khatun', status: EkycRequestStatus.DRAFT, ekycType: 'REGULAR', daysAgo: 2, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name }),
    build({ applicantName: 'Rakib Chowdhury', status: EkycRequestStatus.DRAFT, ekycType: 'SIMPLIFIED', daysAgo: 3, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name }),

    // 4-7 Awaiting branch review, made by a DIFFERENT maker so test-checker can act
    build({ applicantName: 'Sultana Razia', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 4 }),
    build({ applicantName: 'Jahangir Alam', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 5, accountClass: 'ISLAMIC' }),
    build({ applicantName: 'Tahmina Begum', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 5, riskScore: 13, riskBand: 'MEDIUM' }),
    build({ applicantName: 'Nasir Uddin', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'SIMPLIFIED', daysAgo: 6 }),

    // 8 SEGREGATION OF DUTIES: made by test-maker, so test-maker must NOT be able to check it
    build({ applicantName: 'Farzana Yesmin', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 6, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name }),

    // 9-11 Awaiting COGB, branch-checked by a DIFFERENT checker so test-COGB can act
    build({ applicantName: 'Habibur Rahman', status: EkycRequestStatus.COGB_REVIEW, ekycType: 'REGULAR', daysAgo: 7, branchCheckerUserId: OTHER_CHECKER.id, checkerRemark: 'Documents verified. Recommended.' }),
    build({ applicantName: 'Shammi Akhter', status: EkycRequestStatus.COGB_REVIEW, ekycType: 'SIMPLIFIED', daysAgo: 8, branchCheckerUserId: OTHER_CHECKER.id, checkerRemark: 'Simplified tier confirmed.' }),
    build({ applicantName: 'Delwar Hossain', status: EkycRequestStatus.COGB_REVIEW, ekycType: 'REGULAR', daysAgo: 8, branchCheckerUserId: OTHER_CHECKER.id, riskScore: 14, riskBand: 'MEDIUM', checkerRemark: 'Medium band — please review source of funds.' }),

    // 12 PEP + HIGH band — exercises COGB sole authority and the override dialog
    build({ applicantName: 'Anwar Chowdhury', status: EkycRequestStatus.COGB_REVIEW, ekycType: 'REGULAR', daysAgo: 9, branchCheckerUserId: OTHER_CHECKER.id, riskScore: 20, riskBand: 'HIGH', pepConfirmed: true, checkerRemark: 'PEP declared. Branch recommends only — COGB decision required.' }),

    // 13 Screening hold — every approval action must be blocked
    build({ applicantName: 'Kamal Uddin', status: EkycRequestStatus.SCREENING_HOLD, ekycType: 'REGULAR', daysAgo: 10, screeningStatus: ScreeningStatus.POTENTIAL_MATCH }),

    // 14 Face match below threshold — manual review path
    build({ applicantName: 'Rehana Parvin', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 10, faceMatchScore: 61 }),

    // 15 Returned to maker with remarks
    build({ applicantName: 'Sabbir Ahmed', status: EkycRequestStatus.RETURNED_TO_MAKER, ekycType: 'SIMPLIFIED', daysAgo: 11, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, returnCount: 1, branchCheckerUserId: TEST_CHECKER.id, checkerRemark: 'Present address does not match the utility bill. Please correct and resubmit.' }),

    // 16 Rejected — terminal
    build({ applicantName: 'Monir Hossain', status: EkycRequestStatus.REJECTED, ekycType: 'REGULAR', daysAgo: 12, branchCheckerUserId: TEST_CHECKER.id, checkerRemark: 'NID verification failed after three attempts. Rejected.' }),

    // 17 CBS failure — HO ops retry queue
    build({ applicantName: 'Israt Jahan', status: EkycRequestStatus.CBS_FAILED, ekycType: 'REGULAR', daysAgo: 13, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', cbsAttemptCount: 3, cbsLastFailureReason: 'CBS returned 504 on CIF create after 3 idempotent retries' }),

    // 18 Completed cleanly, all three services acknowledged
    build({ applicantName: 'Golam Kibria', status: EkycRequestStatus.COMPLETED, ekycType: 'REGULAR', daysAgo: 14, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012345', services: { DEBIT_CARD: ServiceRequestStatus.ACKNOWLEDGED, CHEQUE_BOOK: ServiceRequestStatus.ACKNOWLEDGED, INTERNET_BANKING: ServiceRequestStatus.ACKNOWLEDGED } }),

    // 19 PARTIAL SUCCESS — account live, debit card failed and open as an exception
    build({ applicantName: 'Ruma Akter', status: EkycRequestStatus.COMPLETED, ekycType: 'REGULAR', daysAgo: 15, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012346', services: { DEBIT_CARD: ServiceRequestStatus.FAILED, CHEQUE_BOOK: ServiceRequestStatus.ACKNOWLEDGED, INTERNET_BANKING: ServiceRequestStatus.ACKNOWLEDGED } }),

    // 20-25 Live Simplified accounts — the KYC Upgrade entry point
    build({ applicantName: 'Selina Hoque', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 16, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012347', upgradeStatus: 'NONE', services: { DEBIT_CARD: ServiceRequestStatus.ACKNOWLEDGED } }),
    build({ applicantName: 'Nabila Sultana', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 17, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012351', upgradeStatus: 'NONE', services: { INTERNET_BANKING: ServiceRequestStatus.ACKNOWLEDGED } }),
    build({ applicantName: 'Samira Islam', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 18, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012352', upgradeStatus: 'NONE', services: { DEBIT_CARD: ServiceRequestStatus.ACKNOWLEDGED, INTERNET_BANKING: ServiceRequestStatus.ACKNOWLEDGED } }),
    build({ applicantName: 'Fahim Ahmed', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 19, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012353', upgradeStatus: 'NONE', services: { CHEQUE_BOOK: ServiceRequestStatus.ACKNOWLEDGED } }),
    build({ applicantName: 'Nusrat Ara', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 20, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012354', upgradeStatus: 'NONE', services: { DEBIT_CARD: ServiceRequestStatus.ACKNOWLEDGED } }),
    build({ applicantName: 'Rashidul Karim', status: EkycRequestStatus.COMPLETED, ekycType: 'SIMPLIFIED', daysAgo: 21, makerUserId: TEST_MAKER.id, makerName: TEST_MAKER.name, branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001', createdAccount: '0101330012355', upgradeStatus: 'NONE', services: { INTERNET_BANKING: ServiceRequestStatus.ACKNOWLEDGED } }),

    // 26-27 Upgrade journeys
    build({ applicantName: 'Mizanur Rahman', status: EkycRequestStatus.BRANCH_REVIEW, ekycType: 'REGULAR', daysAgo: 4, requestKind: 'UPGRADE', upgradeStatus: 'PENDING_APPROVAL' }),
    build({ applicantName: 'Ayesha Siddika', status: EkycRequestStatus.COMPLETED, ekycType: 'REGULAR', daysAgo: 20, requestKind: 'UPGRADE', upgradeStatus: 'UPGRADED', createdAccount: '0101330012348', branchCheckerUserId: OTHER_CHECKER.id, cogbCheckerUserId: 'USR-COGB-001' }),
  ];
}
